<template>
  <div v-if="questions.length > 0 && isPreBid" class="bid-questions-container">
    <!-- Section Header -->
    <div class="questions-header">
      <div class="header-content">
        <div class="header-icon">
          <v-icon size="28" color="white">mdi-help-circle-outline</v-icon>
        </div>
        <div class="header-text">
          <h2>Pre-Bid Questions</h2>
          <p class="subtitle">
            Please answer the following questions. Required questions are marked
            with <span class="required-marker">*</span>
          </p>
        </div>
      </div>
      <div class="header-stats">
        <div class="stat-item">
          <div
            class="stat-value"
            :class="{ complete: answeredCount === totalQuestions }"
          >
            {{ answeredCount }}
          </div>
          <div class="stat-label">Answered</div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <div class="stat-value">{{ totalQuestions }}</div>
          <div class="stat-label">
            {{ totalQuestions === 1 ? 'Question' : 'Questions' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Progress Indicator -->
    <div class="progress-section">
      <div class="progress-info">
        <span class="progress-label">Completion Progress</span>
        <span
          class="progress-percentage"
          :class="{ complete: completionPercentage === 100 }"
        >
          {{ completionPercentage }}%
        </span>
      </div>
      <div class="progress-bar-container">
        <div
          class="progress-bar"
          :class="{ complete: completionPercentage === 100 }"
          :style="{ width: `${completionPercentage}%` }"
        ></div>
      </div>
      <div class="progress-status">
        <v-icon
          size="14"
          :color="completionPercentage === 100 ? 'success' : 'grey'"
        >
          {{
            completionPercentage === 100
              ? 'mdi-check-circle'
              : 'mdi-progress-check'
          }}
        </v-icon>
        <span
          >{{ answeredCount }} of {{ totalQuestions }} questions completed</span
        >
      </div>
    </div>

    <!-- Questions List -->
    <TransitionGroup name="question-list" tag="div" class="questions-list">
      <div
        v-for="(question, index) in questions"
        :key="index"
        class="question-card"
        :class="{
          'question-card--required': question.required && !responses[index],
          'question-card--complete':
            responses[index] && getWordCount(responses[index]) > 0,
          'question-card--exceeded':
            responses[index] &&
            getWordCount(responses[index]) > question.word_limit
        }"
      >
        <!-- Question Header -->
        <div class="question-header">
          <div class="question-badge">
            <span>Q{{ index + 1 }}</span>
          </div>
          <div class="question-content">
            <h3 class="question-text">{{ question.text }}</h3>
            <div class="question-meta">
              <span class="meta-tag word-limit">
                <v-icon size="14">mdi-text-short</v-icon>
                {{ question.word_limit }} words max
              </span>
              <span v-if="question.required" class="meta-tag required">
                <v-icon size="14">mdi-asterisk</v-icon>
                Required
              </span>
              <span v-else class="meta-tag optional">
                <v-icon size="14">mdi-checkbox-blank-circle-outline</v-icon>
                Optional
              </span>
            </div>
          </div>
          <div class="question-status" :class="getStatusClass(index, question)">
            <v-icon size="16">{{ getStatusIcon(index, question) }}</v-icon>
            <span>{{ getStatusText(index, question) }}</span>
          </div>
        </div>

        <!-- Question Body -->
        <div class="question-body">
          <div class="textarea-wrapper">
            <v-textarea
              :model-value="responses[index] || ''"
              :disabled="isDisabledForm"
              variant="outlined"
              placeholder="Enter your response..."
              rows="5"
              hide-details
              class="custom-textarea"
              @update:model-value="updateResponse(index, $event)"
            />
            <div
              class="word-counter"
              :class="getWordCountClass(index, question)"
            >
              <v-icon size="14">mdi-counter</v-icon>
              <span
                >{{ getWordCount(responses[index]) }} /
                {{ question.word_limit }} words</span
              >
            </div>
          </div>

          <!-- Status Message -->
          <Transition name="status-fade">
            <div
              v-if="question.required && !responses[index]"
              class="status-message warning"
            >
              <v-icon size="18">mdi-alert-outline</v-icon>
              <span>This question requires a response</span>
            </div>
            <div
              v-else-if="
                responses[index] &&
                getWordCount(responses[index]) > question.word_limit
              "
              class="status-message error"
            >
              <v-icon size="18">mdi-alert-circle-outline</v-icon>
              <span>
                Response exceeds word limit by
                {{ getWordCount(responses[index]) - question.word_limit }} words
              </span>
            </div>
          </Transition>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';
import { RAW_ASSETS_STATUSES } from '@/constants/statuses';
import { validators } from '../../utils/validators';

interface IProps {
  isDisabledForm: boolean;
}
// eslint-disable-next-line
const props = withDefaults(defineProps<IProps>(), {
  isDisabledForm: false
});

const { getters, commit } = useStore();

// Computed
const auctionStatus = computed<string>(() => {
  const { status } = getters['getAsset'] ?? {};
  return status;
});

const isPreBid = computed<boolean>(() => {
  return [RAW_ASSETS_STATUSES.ACTIVE, RAW_ASSETS_STATUSES.PRE_AUCTION].includes(
    auctionStatus.value
  );
});

// Computed
const questions = computed(() => {
  const qs = getters['getAssetQuestions'] || [];
  console.log('BidViewQuestions: questions', JSON.parse(JSON.stringify(qs)));
  return qs;
});
const responses = computed(() => {
  const rs = getters['getResponsesValues'] || {};
  console.log(
    'BidViewQuestions: responses from getter',
    JSON.parse(JSON.stringify(rs))
  );
  return rs;
});

const totalQuestions = computed(() => questions.value.length);
const answeredCount = computed(() => {
  return Object.values(responses.value).filter(
    (response) =>
      response && typeof response === 'string' && response.trim().length > 0
  ).length;
});
const completionPercentage = computed(() => {
  if (totalQuestions.value === 0) return 0;
  return Math.round((answeredCount.value / totalQuestions.value) * 100);
});

// Rules
const rules = {
  required: (v: string) => validators.required(v)
};

// Methods
const getWordCount = (text: string | undefined): number => {
  if (!text || typeof text !== 'string') return 0;
  const trimmed = text.trim();
  if (trimmed.length === 0) return 0;
  return trimmed.split(/\s+/).length;
};

const updateResponse = (index: number, value: string) => {
  const currentResponses = { ...responses.value };
  if (value && value.trim()) {
    currentResponses[index] = value;
  } else {
    delete currentResponses[index];
  }
  commit('setResponsesValues', currentResponses);
};

const getStatusClass = (index: number, question: any): string => {
  const response = responses.value[index];
  const wordCount = getWordCount(response);

  if (response && wordCount > question.word_limit) {
    return 'status--exceeded';
  }
  if (response && wordCount > 0) {
    return 'status--complete';
  }
  if (question.required) {
    return 'status--required';
  }
  return 'status--pending';
};

const getStatusIcon = (index: number, question: any): string => {
  const response = responses.value[index];
  const wordCount = getWordCount(response);

  if (response && wordCount > question.word_limit) {
    return 'mdi-alert-circle';
  }
  if (response && wordCount > 0) {
    return 'mdi-check-circle';
  }
  if (question.required) {
    return 'mdi-alert';
  }
  return 'mdi-circle-outline';
};

const getStatusText = (index: number, question: any): string => {
  const response = responses.value[index];
  const wordCount = getWordCount(response);

  if (response && wordCount > question.word_limit) {
    return 'Exceeded';
  }
  if (response && wordCount > 0) {
    return 'Complete';
  }
  if (question.required) {
    return 'Required';
  }
  return 'Optional';
};

const getWordCountClass = (index: number, question: any): string => {
  const response = responses.value[index];
  const wordCount = getWordCount(response);

  if (wordCount > question.word_limit) {
    return 'exceeded';
  }
  if (wordCount > 0 && wordCount <= question.word_limit) {
    return 'valid';
  }
  return '';
};
</script>

<style scoped lang="scss">
@import '@/assets/style/_colors.scss';

// Main Container
.bid-questions-container {
  background: linear-gradient(
    135deg,
    $color-ui-white-background 0%,
    $color-ui-gray-super-light 100%
  );
  border-radius: 20px;
  padding: 28px;
  border: 1px solid rgba($color-ui-gray-darker, 0.06);
  box-shadow: 0 4px 24px rgba($color-ui-black, 0.04);
}

// Header Section
.questions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba($color-ui-gray-darker, 0.08);
  flex-wrap: wrap;
  gap: 16px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    $color-ui-blue-primary 0%,
    $color-ui-blue-heading 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba($color-ui-blue-primary, 0.35);
  flex-shrink: 0;
}

.header-text {
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: $color-ui-gray-darker;
    margin: 0;
    letter-spacing: -0.02em;
  }

  .subtitle {
    font-size: 0.9rem;
    color: $color-ui-gray-light;
    margin: 6px 0 0;
    line-height: 1.5;

    .required-marker {
      color: $color-ui-red;
      font-weight: 600;
    }
  }
}

.header-stats {
  display: flex;
  align-items: center;
  gap: 20px;
  background: $color-ui-white;
  padding: 14px 22px;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba($color-ui-black, 0.06);
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: $color-ui-blue-primary;
  line-height: 1;

  &.complete {
    color: $color-ui-green;
  }
}

.stat-label {
  font-size: 0.75rem;
  color: $color-ui-gray-lighter;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 4px;
  font-weight: 500;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: linear-gradient(
    to bottom,
    transparent,
    $color-ui-gray-super-light,
    transparent
  );
}

// Progress Section
.progress-section {
  background: $color-ui-white;
  padding: 20px;
  border-radius: 14px;
  margin-bottom: 24px;
  box-shadow: 0 2px 10px rgba($color-ui-black, 0.04);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: $color-ui-gray-darker;
}

.progress-percentage {
  font-size: 1.1rem;
  font-weight: 700;
  color: $color-ui-blue-primary;

  &.complete {
    color: $color-ui-green;
  }
}

.progress-bar-container {
  height: 10px;
  background: $color-ui-gray-super-light;
  border-radius: 5px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(
    90deg,
    $color-ui-blue-primary 0%,
    $color-ui-blue-heading 100%
  );
  border-radius: 5px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  &.complete {
    background: $color-ui-green;
  }
}

.progress-status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 0.85rem;
  color: $color-ui-gray-light;
}

// Questions List
.questions-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

// Question Card
.question-card {
  background: $color-ui-white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba($color-ui-black, 0.04);
  border: 1px solid rgba($color-ui-gray-darker, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: 0 6px 24px rgba($color-ui-black, 0.08);
    border-color: rgba($color-ui-blue-primary, 0.2);
  }

  &--required {
    border-left: 4px solid $color-ui-yellow;
  }

  &--complete {
    border-left: 4px solid $color-ui-green;
  }

  &--exceeded {
    border-left: 4px solid $color-ui-red;
  }
}

.question-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(
    135deg,
    $color-ui-white-background 0%,
    $color-ui-gray-super-light 100%
  );
  border-bottom: 1px solid rgba($color-ui-gray-darker, 0.05);
  flex-wrap: wrap;
}

.question-badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    $color-ui-blue-primary 0%,
    $color-ui-blue-heading 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 14px rgba($color-ui-blue-primary, 0.3);

  span {
    font-size: 0.9rem;
    font-weight: 700;
    color: $color-ui-white;
  }
}

.question-content {
  flex: 1;
  min-width: 200px;
}

.question-text {
  font-size: 1rem;
  font-weight: 600;
  color: $color-ui-gray-darker;
  margin: 0 0 10px;
  line-height: 1.5;
}

.question-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;

  &.word-limit {
    background: rgba($color-ui-blue-primary, 0.08);
    color: $color-ui-blue-primary;
  }

  &.required {
    background: rgba($color-ui-red, 0.08);
    color: $color-ui-red;
  }

  &.optional {
    background: rgba($color-ui-gray-lighter, 0.2);
    color: $color-ui-gray-light;
  }
}

.question-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  flex-shrink: 0;

  &.status--complete {
    background: rgba($color-ui-green, 0.1);
    color: $color-ui-green;
  }

  &.status--required {
    background: rgba($color-ui-yellow, 0.1);
    color: $color-ui-yellow-dark;
  }

  &.status--exceeded {
    background: rgba($color-ui-red, 0.1);
    color: $color-ui-red;
  }

  &.status--pending {
    background: rgba($color-ui-gray-lighter, 0.2);
    color: $color-ui-gray-light;
  }
}

.question-body {
  padding: 20px;
}

.textarea-wrapper {
  position: relative;
}

.custom-textarea {
  :deep(.v-field) {
    border-radius: 12px;
    background: $color-ui-white-background;
    transition: all 0.2s ease;
  }

  :deep(.v-field:hover) {
    background: $color-ui-gray-super-light;
  }

  :deep(.v-field--focused) {
    background: $color-ui-white;
    box-shadow: 0 0 0 3px rgba($color-ui-blue-primary, 0.15);
  }

  :deep(.v-field__outline) {
    color: rgba($color-ui-gray-darker, 0.12);
  }

  :deep(.v-field--focused .v-field__outline) {
    color: $color-ui-blue-primary;
    border-width: 2px;
  }
}

.word-counter {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  padding: 6px 12px;
  background: $color-ui-gray-super-light;
  border-radius: 8px;
  font-size: 0.8rem;
  color: $color-ui-gray-light;
  width: fit-content;

  &.valid {
    background: rgba($color-ui-green, 0.1);
    color: $color-ui-green;
  }

  &.exceeded {
    background: rgba($color-ui-red, 0.1);
    color: $color-ui-red;
    font-weight: 600;
  }
}

.status-message {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 500;

  &.warning {
    background: rgba($color-ui-yellow, 0.08);
    color: $color-ui-yellow-dark;
    border: 1px solid rgba($color-ui-yellow, 0.2);
  }

  &.error {
    background: rgba($color-ui-red, 0.08);
    color: $color-ui-red;
    border: 1px solid rgba($color-ui-red, 0.2);
  }

  &.success {
    background: rgba($color-ui-green, 0.08);
    color: $color-ui-green;
    border: 1px solid rgba($color-ui-green, 0.2);
  }
}

// Transitions
.question-list-enter-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.question-list-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.question-list-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.question-list-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.status-fade-enter-active,
.status-fade-leave-active {
  transition: all 0.3s ease;
}

.status-fade-enter-from,
.status-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

// Responsive
@media (max-width: 768px) {
  .bid-questions-container {
    padding: 20px;
    border-radius: 16px;
  }

  .questions-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-stats {
    width: 100%;
    justify-content: center;
  }

  .question-header {
    flex-direction: column;
    gap: 12px;
  }

  .question-status {
    align-self: flex-start;
  }
}
</style>
